import { useQuery } from '@apollo/client';
import {
  Box,
  ContentBlock,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
} from 'braid-design-system';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Urls from 'src/config/routes';
import { GetBuyerEmailAddressDocument } from 'src/generated/graphql';
import { useFetch } from 'src/hooks/fetch';
import useOverflowScroll from 'src/hooks/useOverflowScroll';
import {
  getAccountFetchStatus,
  getAccountData,
  selectUserAccountType,
} from 'src/store/account';

import ProfileDetails from './components/ProfileDetails/ProfileDetails';
import ProfileHeader from './components/ProfileHeader';
import SavedBusinesses from './components/SavedBusinesses/SavedBusinesses';
import SavedSearches from './components/SavedSearches/SavedSearches';
import { mapRouteToQuery } from './mapping/mapping';

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const routeQuery = mapRouteToQuery(location.pathname);

  const getBuyerEmailAddressResult = useQuery(GetBuyerEmailAddressDocument);
  const getTabContentResult = useQuery(routeQuery);
  const getAccountDataResult = useFetch(getAccountFetchStatus, getAccountData);

  const userAccountType = useSelector(selectUserAccountType);

  // Avoid the scrollbar appearing/disappearing causing layout shift
  useOverflowScroll();

  // Use of "previousData" is to avoid flickering while refetching the same data.
  // The optimal solution here would be to lean into the Apollo cache. I was not
  // able to get this to work
  if (
    (!getBuyerEmailAddressResult.previousData &&
      getBuyerEmailAddressResult.loading) ||
    (!getTabContentResult.previousData && getTabContentResult.loading) ||
    !getAccountDataResult.hasLoaded
  ) {
    return <CustomLoader />;
  }

  return (
    <Box>
      <ProfileHeader
        userAccountType={userAccountType}
        emailAddress={
          // Avoid flickering while refetching buyer data
          getBuyerEmailAddressResult.previousData?.buyer?.emailAddress ??
          getBuyerEmailAddressResult.data?.buyer?.emailAddress
        }
      />

      <Box paddingY="medium">
        <TabsProvider
          id="dashboardTabs"
          selectedItem={location.pathname}
          onChange={(index, item) => item && navigate(item)}
        >
          <Box paddingTop="medium" id="profile-page-header-tabs-navigation">
            <ContentBlock>
              <Tabs label="profileTabs">
                <Tab item={Urls.PROFILE_SAVED_BUSINESSES}>
                  <Box
                    id="profile-saved-businesses-tab"
                    paddingX={{ mobile: 'none', tablet: 'xlarge' }}
                  >
                    Saved businesses
                  </Box>
                </Tab>
                <Tab item={Urls.PROFILE_SAVED_SEARCHES}>
                  <Box
                    id="profile-saved-searches-tab"
                    paddingX={{ mobile: 'none', tablet: 'xlarge' }}
                  >
                    Saved searches
                  </Box>
                </Tab>
                <Tab item={Urls.PROFILE_DETAILS}>
                  <Box
                    id="profile-my-deatails-tab"
                    paddingX={{ mobile: 'none', tablet: 'xlarge' }}
                  >
                    My details
                  </Box>
                </Tab>
              </Tabs>
            </ContentBlock>
          </Box>

          <ContentBlock>
            <TabPanels>
              <TabPanel item={Urls.PROFILE_SAVED_BUSINESSES}>
                <Box id="profile-saved-businesses-page">
                  <SavedBusinesses />
                </Box>
              </TabPanel>
              <TabPanel item={Urls.PROFILE_SAVED_SEARCHES}>
                <Box id="profile-saved-searches-page">
                  <SavedSearches />
                </Box>
              </TabPanel>
              <TabPanel item={Urls.PROFILE_DETAILS}>
                <Box id="profile-my-deatails-page">
                  <ProfileDetails userAccountType={userAccountType} />
                </Box>
              </TabPanel>
            </TabPanels>
          </ContentBlock>
        </TabsProvider>
      </Box>
    </Box>
  );
};

export default Profile;
